import React, { useState, useEffect } from 'react';
import { DashboardStats } from '../../types/admin';
import { SkeletonStatCard } from '../common/SkeletonLoader';

const StatCard: React.FC<{
  title: string;
  value: string | number;
  change?: number;
  icon: JSX.Element;
  index?: number;
  iconBg: string;
  iconColor: string;
}> = ({ title, value, change, icon, index = 0, iconBg, iconColor }) => {
  const hasChange = change !== undefined && change !== 0;
  const isPositive = change !== undefined && change >= 0;

  return (
    <div
      className="group bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent hover:border-gray-300 dark:hover:border-transparent hover:shadow-md dark:hover:shadow-purple-500/10 transition-all duration-200 p-3.5 sm:p-4 animate-scaleIn"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`w-8 h-8 ${iconBg} rounded-xl flex items-center justify-center`}>
          <span className={iconColor}>
            {icon}
          </span>
        </div>
        {hasChange && (
          <div className={`flex items-center gap-0.5 text-[11px] font-medium ${
            isPositive
              ? 'text-emerald-600 dark:text-emerald-400'
              : 'text-red-500 dark:text-red-400'
          }`}>
            <svg aria-hidden="true" className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isPositive ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
              )}
            </svg>
            <span>{Math.abs(change)}%</span>
          </div>
        )}
      </div>
      <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-tight">
        {value}
      </p>
      <p className="text-[11px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 mt-0.5">
        {title}
      </p>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeUsers: 0,
    totalSessions: 0,
    revenueThisMonth: 0,
    userGrowth: 0,
    sessionGrowth: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate API call delay
    const timer = setTimeout(() => {
      // Mock data - replace with real API calls
      setStats({
        totalUsers: 1247,
        activeUsers: 892,
        totalSessions: 3456,
        revenueThisMonth: 15420,
        userGrowth: 12.5,
        sessionGrowth: 8.3
      });
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  return (
    <div className="flex-1 h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex flex-col transition-colors duration-200 relative overflow-hidden">
      {/* Background watermark logo */}
      <div className="absolute top-0 right-0 w-full h-full pointer-events-none flex items-center justify-end">
        <div className="relative w-[800px] h-[800px] -mr-32">
          <img
            src="/icon-virtualy.png"
            alt=""
            className="w-full h-full object-contain opacity-[0.04] dark:opacity-[0.05] transform rotate-[20deg]"
            style={{ filter: 'grayscale(20%)' }}
          />
        </div>
      </div>

      {/* Dark mode accent elements */}
      <div className="absolute inset-0 pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-200">
        <div className="absolute top-20 left-20 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="flex-1 flex flex-col overflow-y-auto bg-transparent relative z-10">
        <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
      <div className="mb-4 sm:mb-5 animate-fadeIn">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          {getGreeting()}, Admin
        </h1>
        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
          Here's what's happening with your platform today
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3 mb-4 sm:mb-5">
        {loading ? (
          <>
            <SkeletonStatCard />
            <SkeletonStatCard />
            <SkeletonStatCard />
            <SkeletonStatCard />
          </>
        ) : (
          <>
          <StatCard
            title="Total Users"
            value={stats.totalUsers.toLocaleString()}
            change={stats.userGrowth}
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            }
            index={0}
            iconBg="bg-blue-50 dark:bg-blue-900/20"
            iconColor="text-blue-500 dark:text-blue-400"
          />
          <StatCard
            title="Active Users"
            value={stats.activeUsers.toLocaleString()}
            change={stats.userGrowth * 0.8}
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            index={1}
            iconBg="bg-emerald-50 dark:bg-emerald-900/20"
            iconColor="text-emerald-500 dark:text-emerald-400"
          />
          <StatCard
            title="Total Sessions"
            value={stats.totalSessions.toLocaleString()}
            change={stats.sessionGrowth}
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
            index={2}
            iconBg="bg-violet-50 dark:bg-violet-900/20"
            iconColor="text-violet-500 dark:text-violet-400"
          />
          <StatCard
            title="Revenue"
            value={`$${stats.revenueThisMonth.toLocaleString()}`}
            change={15.2}
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            }
            index={3}
            iconBg="bg-amber-50 dark:bg-amber-900/20"
            iconColor="text-amber-500 dark:text-amber-400"
          />
          </>
        )}
      </div>

      {/* Content with Sidebar */}
      <div className="flex flex-col xl:flex-row gap-3 sm:gap-4">
        {/* Main Content Area */}
        <div className="flex-1 min-w-0">
          <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent overflow-hidden">
            <div className="px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-100 dark:border-gray-700/50">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700/50 rounded-xl flex items-center justify-center">
                  <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-900 dark:text-white">Recent Activity</h2>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">Latest platform activity</p>
                </div>
              </div>
            </div>

            <div className="p-3 sm:p-4 space-y-1.5">
              {[
                { user: 'John Doe', action: 'completed coaching session', time: '2h ago', color: 'bg-blue-500' },
                { user: 'Sarah Smith', action: 'upgraded to premium', time: '4h ago', color: 'bg-emerald-500' },
                { user: 'Mike Johnson', action: 'set new goal', time: '6h ago', color: 'bg-violet-500' },
                { user: 'Emily Davis', action: 'joined platform', time: '1d ago', color: 'bg-amber-500' },
                { user: 'Alex Chen', action: 'created new scenario', time: '2d ago', color: 'bg-cyan-500' },
                { user: 'Rachel Green', action: 'completed training module', time: '3d ago', color: 'bg-pink-500' }
              ].map((activity, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-700/30 hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors"
                >
                  <div className={`w-8 h-8 rounded-full ${activity.color} flex items-center justify-center flex-shrink-0`}>
                    <span className="text-white text-[11px] font-semibold">
                      {activity.user.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                      {activity.user}
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                      {activity.action}
                    </p>
                  </div>
                  <span className="text-[11px] text-gray-400 dark:text-gray-500 flex-shrink-0 hidden sm:block">
                    {activity.time}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="w-full xl:w-[280px] 2xl:w-[300px] shrink-0 space-y-3 sm:space-y-4">

          {/* Quick Actions */}
          <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700/50">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-violet-50 dark:bg-violet-900/20 rounded-lg flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xs font-bold text-gray-900 dark:text-white">Quick Actions</h3>
              </div>
            </div>
            <div className="p-2.5 space-y-1">
              {[
                { label: 'Add New User', icon: 'M12 6v6m0 0v6m0-6h6m-6 0H6', bg: 'bg-blue-100 dark:bg-blue-900/30', color: 'text-blue-600 dark:text-blue-400' },
                { label: 'View Reports', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', bg: 'bg-gray-100 dark:bg-gray-700/50', color: 'text-gray-600 dark:text-gray-400' },
                { label: 'System Settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z', bg: 'bg-gray-100 dark:bg-gray-700/50', color: 'text-gray-600 dark:text-gray-400' },
              ].map((action) => (
                <button key={action.label} className="w-full flex items-center gap-2.5 px-2.5 py-2 bg-gray-50 dark:bg-gray-700/30 hover:bg-gray-100 dark:hover:bg-gray-700/60 rounded-xl transition-all duration-200 group">
                  <div className={`w-7 h-7 ${action.bg} rounded-lg flex items-center justify-center`}>
                    <svg className={`w-3.5 h-3.5 ${action.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={action.icon} />
                    </svg>
                  </div>
                  <span className="text-xs font-medium text-gray-900 dark:text-white">{action.label}</span>
                  <svg aria-hidden="true" className="w-3.5 h-3.5 text-gray-400 ml-auto group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          </div>

          {/* System Health */}
          <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700/50">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xs font-bold text-gray-900 dark:text-white">System Health</h3>
              </div>
            </div>
            <div className="p-2.5 space-y-1">
              {['Database', 'API Server', 'Storage'].map((service) => (
                <div key={service} className="flex items-center justify-between px-2.5 py-2 rounded-xl bg-gray-50 dark:bg-gray-700/40">
                  <div className="flex items-center gap-2">
                    <div aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400"></div>
                    <span className="text-xs font-medium text-gray-900 dark:text-white">{service}</span>
                  </div>
                  <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400">Healthy</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
